import * as React from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Chart from "chart.js/auto";

interface NewsArticleProps {
  data: string;
  title?: string;
  snippet?: string;
  author?: string;
  publishedAt?: string;
  publishedAtIso?: string;
  tags?: string[];
  slug?: string;
}

function authorAvatarUrl(author: string) {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=random`;
}

function readingTimeMinutes(markdown: string) {
  if (!markdown) return 1;
  const words = markdown.replace(/[`*_>#~\-]/g, " ").trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
}

const ChartComponent: React.FC<{ content: string }> = ({ content }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    try {
      const chartData = JSON.parse(content);
      chartRef.current?.destroy();
      chartRef.current = new Chart(canvasRef.current, {
        type: chartData.type || "line",
        data: chartData.data,
        options: chartData.options || {},
      });
    } catch (error) {
      console.error("Failed to parse news chart data:", error);
    }
    return () => {
      chartRef.current?.destroy();
    };
  }, [content]);

  return (
    <div className="news-md-chart">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default function NewsArticle({
  data,
  title,
  snippet,
  author,
  publishedAt,
  publishedAtIso,
  tags,
  slug,
}: NewsArticleProps) {
  const readingMinutes = useMemo(() => readingTimeMinutes(data || ""), [data]);
  const [shareFeedback, setShareFeedback] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string>("");
  const [showStickyHeader, setShowStickyHeader] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      setShareUrl(window.location.href);
    }
  }, [slug]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onScroll = () => setShowStickyHeader(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareFeedback("Link copied");
    } catch {
      setShareFeedback("Press Cmd+C to copy");
    }
    setTimeout(() => setShareFeedback(null), 1800);
  };

  const twitterUrl = useMemo(() => {
    const text = title ? `${title} | Decode Investing News` : "Decode Investing News";
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
  }, [title, shareUrl]);

  const linkedInUrl = useMemo(() => {
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
  }, [shareUrl]);

  return (
    <>
      <style>{styles}</style>

      <div className={`news-article-sticky ${showStickyHeader ? "is-visible" : ""}`} aria-hidden={!showStickyHeader}>
        <div className="news-article-sticky-inner">
          <a href="/news" className="news-article-sticky-back" aria-label="Back to News">News</a>
          <div className="news-article-sticky-title" title={title}>{title}</div>
          <div className="news-article-sticky-actions">
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="news-share-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2H21l-6.55 7.49L22 22h-6.79l-4.83-6.21L4.8 22H2.04l7.04-8.05L2 2h6.91l4.36 5.71L18.24 2zm-2.38 18h1.74L8.22 4H6.4l9.46 16z"/></svg>
            </a>
            <a href={linkedInUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="news-share-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 17.5V10.2H6.06v7.3h2.28zM7.2 9.18a1.32 1.32 0 1 0 0-2.64 1.32 1.32 0 0 0 0 2.64zm10.78 8.32v-4.18c0-2.24-1.2-3.28-2.8-3.28-1.3 0-1.88.72-2.2 1.22V10.2H10.7c.04.66 0 7.3 0 7.3h2.28V13.4c0-.2.02-.4.08-.54.16-.4.52-.82 1.14-.82.8 0 1.12.6 1.12 1.5v3.96h2.66z"/></svg>
            </a>
            <button onClick={copyLink} aria-label="Copy link" className="news-share-icon news-share-button" type="button">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>
            </button>
          </div>
        </div>
      </div>

      <article className="news-article">
        <nav className="news-article-nav">
          <a href="/news" className="news-article-back">← Back to News</a>
        </nav>

        <header className="news-article-header">
          {tags && tags.length > 0 && (
            <div className="news-article-tags">
              {tags.map((tag) => (
                <span key={tag} className="news-article-tag">{tag}</span>
              ))}
            </div>
          )}

          {title && <h1 className="news-article-title">{title}</h1>}

          {snippet && <p className="news-article-deck">{snippet}</p>}

          <div className="news-article-byline">
            {author && (
              <div className="news-article-author">
                <img src={authorAvatarUrl(author)} alt={author} className="news-article-avatar" />
                <span className="news-article-author-name">{author}</span>
              </div>
            )}
            <div className="news-article-meta">
              {publishedAt && (
                <time dateTime={publishedAtIso} className="news-article-date">{publishedAt}</time>
              )}
              <span className="news-article-meta-dot">·</span>
              <span className="news-article-reading-time">{readingMinutes} min read</span>
            </div>
          </div>

          <div className="news-article-share">
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="news-share-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2H21l-6.55 7.49L22 22h-6.79l-4.83-6.21L4.8 22H2.04l7.04-8.05L2 2h6.91l4.36 5.71L18.24 2zm-2.38 18h1.74L8.22 4H6.4l9.46 16z"/></svg>
              <span>Post</span>
            </a>
            <a href={linkedInUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="news-share-pill">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 17.5V10.2H6.06v7.3h2.28zM7.2 9.18a1.32 1.32 0 1 0 0-2.64 1.32 1.32 0 0 0 0 2.64zm10.78 8.32v-4.18c0-2.24-1.2-3.28-2.8-3.28-1.3 0-1.88.72-2.2 1.22V10.2H10.7c.04.66 0 7.3 0 7.3h2.28V13.4c0-.2.02-.4.08-.54.16-.4.52-.82 1.14-.82.8 0 1.12.6 1.12 1.5v3.96h2.66z"/></svg>
              <span>Share</span>
            </a>
            <button onClick={copyLink} className="news-share-pill" type="button" aria-label="Copy link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 1 0-7.07-7.07l-1.5 1.5"/><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07l1.5-1.5"/></svg>
              <span>{shareFeedback ?? "Copy link"}</span>
            </button>
          </div>
        </header>

        <div className="news-article-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({ node, ...props }) => <h1 className="news-md-h1" {...props} />,
              h2: ({ node, ...props }) => <h2 className="news-md-h2" {...props} />,
              h3: ({ node, ...props }) => <h3 className="news-md-h3" {...props} />,
              p: ({ node, ...props }) => <p className="news-md-p" {...props} />,
              ul: ({ node, ...props }) => <ul className="news-md-ul" {...props} />,
              ol: ({ node, ...props }) => <ol className="news-md-ol" {...props} />,
              li: ({ node, ...props }) => <li className="news-md-li" {...props} />,
              blockquote: ({ node, ...props }) => <blockquote className="news-md-blockquote" {...props} />,
              a: ({ node, ...props }) => <a className="news-md-a" {...props} />,
              img: ({ node, ...props }) => <img className="news-md-img" loading="lazy" {...props} />,
              code: ({ node, inline, className, children, ...props }: any) => {
                if (!inline && /language-chart/.test(className || "")) {
                  return <ChartComponent content={String(children).replace(/\n$/, "")} />;
                }
                return inline ? (
                  <code className="news-md-code-inline" {...props}>{children}</code>
                ) : (
                  <pre className="news-md-pre"><code className={className} {...props}>{children}</code></pre>
                );
              },
              hr: () => <hr className="news-md-hr" />,
              table: ({ node, ...props }) => <div className="news-md-table-wrap"><table className="news-md-table" {...props} /></div>,
            }}
          >
            {data}
          </ReactMarkdown>
        </div>

        <footer className="news-article-footer">
          <a href="/news" className="news-article-back">← More from Decode Investing News</a>
        </footer>
      </article>
    </>
  );
}

const styles = `
  .news-article {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
    max-width: 760px;
    margin: 0 auto;
    padding: 24px 24px 96px;
    color: rgb(41, 41, 41);
  }

  .news-article-nav {
    margin-bottom: 24px;
  }

  .news-article-back {
    color: rgb(60, 60, 60);
    text-decoration: none;
    font-size: 15px;
    font-weight: 500;
    transition: color 0.15s ease;
  }

  .news-article-back:hover {
    color: rgb(0, 0, 0);
    text-decoration: underline;
  }

  .news-article-header {
    margin-bottom: 32px;
  }

  .news-article-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 16px;
  }

  .news-article-tag {
    display: inline-block;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    border-radius: 999px;
    background: rgba(99, 102, 241, 0.1);
    color: rgb(79, 70, 229);
  }

  .news-article-title {
    font-size: clamp(30px, 5vw, 46px);
    font-weight: 800;
    line-height: 1.15;
    letter-spacing: -0.022em;
    color: rgb(20, 20, 20);
    margin: 0 0 16px;
  }

  .news-article-deck {
    font-size: clamp(17px, 2vw, 21px);
    line-height: 1.55;
    color: rgb(85, 85, 85);
    margin: 0 0 24px;
  }

  .news-article-byline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 14px;
    padding-bottom: 18px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  }

  .news-article-author {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .news-article-avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    object-fit: cover;
  }

  .news-article-author-name {
    font-size: 15px;
    font-weight: 600;
    color: rgb(25, 25, 25);
  }

  .news-article-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: rgb(120, 120, 120);
  }

  .news-article-meta-dot {
    color: rgb(180, 180, 180);
  }

  .news-article-share {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 18px;
  }

  .news-share-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.12);
    background: transparent;
    color: rgb(40, 40, 40);
    font-size: 13px;
    font-weight: 500;
    text-decoration: none;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease;
  }

  .news-share-pill:hover {
    background-color: rgba(0, 0, 0, 0.04);
    border-color: rgba(0, 0, 0, 0.2);
    text-decoration: none;
  }

  .news-article-body {
    line-height: 1.8;
  }

  .news-md-h1 {
    font-size: clamp(28px, 4vw, 40px);
    font-weight: 800;
    margin: 56px 0 24px;
    line-height: 1.2;
    letter-spacing: -0.018em;
    color: rgb(20, 20, 20);
  }

  .news-md-h2 {
    font-size: clamp(22px, 3vw, 30px);
    font-weight: 700;
    margin: 48px 0 20px;
    line-height: 1.25;
    letter-spacing: -0.014em;
    color: rgb(20, 20, 20);
  }

  .news-md-h3 {
    font-size: clamp(18px, 2.4vw, 22px);
    font-weight: 700;
    margin: 36px 0 16px;
    line-height: 1.3;
    color: rgb(20, 20, 20);
  }

  .news-md-p {
    font-size: 19px;
    margin-bottom: 28px;
    color: rgb(40, 40, 40);
    line-height: 1.8;
    font-weight: 400;
  }

  .news-md-ul,
  .news-md-ol {
    margin: 0 0 28px;
    padding-left: 30px;
  }

  .news-md-li {
    font-size: 19px;
    margin-bottom: 12px;
    color: rgb(40, 40, 40);
    line-height: 1.75;
  }

  .news-md-blockquote {
    margin: 24px 0 32px;
    padding: 14px 22px;
    border-left: 3px solid rgb(99, 102, 241);
    background: rgba(99, 102, 241, 0.05);
    color: rgb(45, 45, 45);
    font-size: 19px;
    line-height: 1.7;
    border-radius: 0 12px 12px 0;
  }

  .news-md-a {
    color: rgb(79, 70, 229);
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .news-md-a:hover {
    color: rgb(55, 48, 163);
  }

  .news-md-img {
    width: 100%;
    height: auto;
    border-radius: 14px;
    margin: 12px 0 32px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.06);
  }

  .news-md-code-inline {
    font-family: "SF Mono", Menlo, Consolas, monospace;
    font-size: 0.92em;
    background: rgba(0, 0, 0, 0.06);
    padding: 2px 6px;
    border-radius: 6px;
  }

  .news-md-pre {
    background: #0f1115;
    color: #e6e6e6;
    padding: 18px 20px;
    border-radius: 12px;
    overflow-x: auto;
    margin: 0 0 32px;
    font-size: 14px;
    line-height: 1.6;
  }

  .news-md-hr {
    border: 0;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    margin: 40px 0;
  }

  .news-md-table-wrap {
    overflow-x: auto;
    margin-bottom: 32px;
  }

  .news-md-chart {
    position: relative;
    margin: 12px 0 32px;
    padding: 18px 20px;
    border-radius: 14px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    background: rgba(0, 0, 0, 0.015);
  }

  .news-md-chart canvas {
    width: 100% !important;
    height: auto !important;
    max-height: 420px;
  }

  .news-md-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 16px;
  }

  .news-md-table th,
  .news-md-table td {
    border: 1px solid rgba(0, 0, 0, 0.1);
    padding: 10px 14px;
    text-align: left;
  }

  .news-md-table th {
    background: rgba(0, 0, 0, 0.03);
    font-weight: 600;
  }

  .news-article-footer {
    margin-top: 56px;
    padding-top: 24px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
  }

  .news-article-sticky {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 60;
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: saturate(180%) blur(12px);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    transform: translateY(-100%);
    transition: transform 0.18s ease;
  }

  .news-article-sticky.is-visible {
    transform: translateY(0);
  }

  .news-article-sticky-inner {
    max-width: 1120px;
    margin: 0 auto;
    padding: 10px 24px;
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .news-article-sticky-back {
    color: rgb(40, 40, 40);
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    flex-shrink: 0;
  }

  .news-article-sticky-title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
    color: rgb(25, 25, 25);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .news-article-sticky-actions {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
  }

  .news-share-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    color: rgb(60, 60, 60);
    background: transparent;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
  }

  .news-share-icon:hover {
    background-color: rgba(0, 0, 0, 0.06);
    color: rgb(0, 0, 0);
  }

  .news-share-button {
    padding: 0;
  }

  @media (max-width: 640px) {
    .news-article {
      padding: 16px 16px 72px;
    }

    .news-md-p,
    .news-md-li {
      font-size: 17px;
      line-height: 1.75;
    }

    .news-article-byline {
      gap: 12px;
    }

    .news-article-sticky-inner {
      padding: 10px 16px;
    }

    .news-article-sticky-title {
      font-size: 13px;
    }
  }

  .dark-mode .news-article {
    color: #d8d8d8;
  }

  .dark-mode .news-article-back,
  .dark-mode .news-article-author-name,
  .dark-mode .news-md-h1,
  .dark-mode .news-md-h2,
  .dark-mode .news-md-h3,
  .dark-mode .news-article-title {
    color: #ffffff;
  }

  .dark-mode .news-article-deck,
  .dark-mode .news-md-p,
  .dark-mode .news-md-li,
  .dark-mode .news-article-meta {
    color: #c8ccd5;
  }

  .dark-mode .news-article-byline,
  .dark-mode .news-article-footer,
  .dark-mode .news-md-hr {
    border-color: #2a272f;
  }

  .dark-mode .news-article-tag {
    background: rgba(182, 109, 255, 0.15);
    color: #d6b6ff;
  }

  .dark-mode .news-share-pill {
    color: #e8e8e8;
    border-color: rgba(255, 255, 255, 0.16);
  }

  .dark-mode .news-share-pill:hover {
    background-color: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.28);
  }

  .dark-mode .news-md-blockquote {
    background: rgba(182, 109, 255, 0.07);
    border-left-color: #b66dff;
    color: #d8d8d8;
  }

  .dark-mode .news-md-a {
    color: #c9aaff;
  }

  .dark-mode .news-md-a:hover {
    color: #ffffff;
  }

  .dark-mode .news-md-code-inline {
    background: rgba(255, 255, 255, 0.08);
  }

  .dark-mode .news-md-img {
    border-color: #322f2f;
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.4);
  }

  .dark-mode .news-md-table th {
    background: rgba(255, 255, 255, 0.04);
  }

  .dark-mode .news-md-table th,
  .dark-mode .news-md-table td {
    border-color: rgba(255, 255, 255, 0.1);
  }

  .dark-mode .news-md-chart {
    border-color: #322f2f;
    background: rgba(255, 255, 255, 0.02);
  }

  .dark-mode .news-article-sticky {
    background: rgba(20, 18, 24, 0.92);
    border-bottom-color: #2a272f;
  }

  .dark-mode .news-article-sticky-back,
  .dark-mode .news-article-sticky-title {
    color: #f1f1f1;
  }

  .dark-mode .news-share-icon {
    color: #d8d8d8;
  }

  .dark-mode .news-share-icon:hover {
    background-color: rgba(255, 255, 255, 0.08);
    color: #ffffff;
  }
`;
